import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import onaGradientBg from "@/assets/ona-gradient.webp";
import { cn } from "@/components/podkit/lib/cn";
import { Input } from "@/components/podkit/forms/Input";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { toast } from "@/components/podkit/toasts/use-toast";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useCallback, useId, useMemo, useState, type FC } from "react";

type OnaWaitlistLayoutProps = {
    className?: string;
    userEmail?: string;
};

export const OnaWaitlistLayout: FC<OnaWaitlistLayoutProps> = ({ className, userEmail }) => {
    const segmentTrack = useSegmentTrack();
    const [email, setEmail] = useState(userEmail || "");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [joined, setJoined] = useState(false);
    const emailInputId = useId();

    const isEmail = useMemo(() => /.+@.+\..+/.test(email), [email]);

    const onJoin = useCallback(() => {
        if (!isEmail || isSubmitting) return;
        try {
            setIsSubmitting(true);
            segmentTrack("waitlist_joined", { feature: "Ona", email });
            setJoined(true);
            toast({ title: "You're on the waitlist!", description: "We'll reach out to you soon." });
        } finally {
            setIsSubmitting(false);
        }
    }, [email, isEmail, isSubmitting, segmentTrack]);

    return (
        <div
            data-testid="ona-waitlist-login-page"
            className={cn(
                "flex h-full min-h-screen w-full flex-col items-center justify-between bg-cover bg-center bg-no-repeat p-6 text-gray-900",
                className,
            )}
            style={{ backgroundImage: `url(${onaGradientBg})` }}
        >
            <div className="flex flex-1 items-center justify-center">
                <div className="min-w-128 m-8 flex flex-col items-center rounded-2xl bg-white px-6 pb-8 shadow-2xl dark:bg-surface-glass dark:text-white">
                    <OnaWordmark className="m-2 mb-4 mt-14 h-10 w-auto" aria-hidden="true" />
                    <Text className="mb-4 text-center text-inherit opacity-60">Move beyond your IDE</Text>
                    <Text className="mb-2 text-center text-inherit opacity-80">
                        Ona access is limited right now. Join the waitlist and we&apos;ll notify you when it&apos;s
                        available.
                    </Text>
                    <div className="my-4 flex w-full flex-col gap-2">
                        <label className="text-sm" htmlFor={emailInputId}>
                            Email
                        </label>
                        <Input
                            id={emailInputId}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                        />
                    </div>
                    <Button
                        disabled={!isEmail || joined || isSubmitting}
                        onClick={onJoin}
                        className="mt-2 w-full"
                        data-testid="ona-waitlist-join"
                        data-tracking-id="join-waitlist-ona-waitlist-layout"
                    >
                        {joined ? "Joined" : "Join waitlist"}
                    </Button>
                </div>
            </div>
        </div>
    );
};
